/**
 * What each field actually does, in plain words, with the engine function that
 * establishes it.
 *
 * The owner's note that started this: "Traits shift SPECIAL on read" is
 * technically exact and tells a player nothing. The rule followed here is that
 * an explanation has to survive being read by someone who has never opened the
 * engine source — so it says what happens to *their character*, and the citation
 * rides along at the end for anyone who wants to check.
 *
 * Two things this module will not do:
 *
 *   - It will not describe a mechanic the engine source does not establish.
 *     Poison, for instance, has no level table and no stat penalties anywhere in
 *     `critter.cc`; the note says that, instead of repeating the folklore.
 *   - It will not paper over a knob the save cannot see. Where a number depends
 *     on something outside the file, the note says which and `rules.UNESTABLISHED`
 *     carries the detail.
 */

import {
  POISON_DAMAGE_PER_TICK,
  POISON_DECAY_PER_TICK,
  RADIATION_INDICATOR_THRESHOLD,
  RADIATION_LEVELS,
  SKILL_COST_BANDS,
  SKILL_DISPLAY_CAP,
  maxSelectablePerks,
} from './rules';
import { forGame } from './constants-fo1';
import type { SaveGameType } from './gamedefs';

export type Explanation = {
  /** One sentence, the thing a player needs. */
  summary: string;
  /** Important equations that should remain visible at a glance. */
  formulas?: { label: string; value: string }[];
  /** Compact comparisons, thresholds or bands. */
  table?: { columns: string[]; rows: string[][] };
  /** Supporting rules, disclosed on demand as bullets. */
  detail?: string[];
  /** `engine/src/stat.cc critterUpdateDerivedStats` — always a real symbol. */
  source: string;
};

const STAT_EXPLANATIONS: Record<string, Explanation> = {
  STRENGTH: {
    summary: 'Carries your melee damage, how much you can hold, and part of your hit points.',
    formulas: [
      { label: 'Maximum HP base', value: 'base STR + 2 × base END + 15' },
      { label: 'Melee damage', value: 'max(STR − 5, 1)' },
      { label: 'Carry weight', value: '25 × STR + 25' },
    ],
    detail: [
      'Weapons also have their own minimum Strength; falling short costs accuracy. That check is in '
        + 'the combat code, not the stat block, so this editor does not show it per weapon.',
    ],
    source: 'engine/src/stat.cc critterUpdateDerivedStats; item.cc weaponGetMinStrength',
  },
  PERCEPTION: {
    summary: 'Sets your combat sequence, and how far you can shoot accurately.',
    formulas: [{ label: 'Sequence', value: '2 × PER' }],
    detail: ['Also governs First Aid, Doctor, Lockpick and Traps.'],
    source: 'engine/src/stat.cc critterUpdateDerivedStats; skill.cc gSkillDescriptions',
  },
  ENDURANCE: {
    summary: 'Hit points, healing rate, and both resistances.',
    formulas: [
      { label: 'Maximum HP base', value: 'base STR + 2 × base END + 15' },
      { label: 'Healing rate', value: 'max(END ÷ 3, 1)' },
      { label: 'Rad resistance', value: '2 × END' },
      { label: 'Poison resistance', value: '5 × END' },
    ],
    source: 'engine/src/stat.cc critterUpdateDerivedStats',
  },
  CHARISMA: {
    summary: 'Governs Speech and Barter, and how many companions will follow you.',
    detail: ['The party-size rule lives in the party scripts rather than the stat block, so it is not shown here.'],
    source: 'engine/src/skill.cc gSkillDescriptions',
  },
  INTELLIGENCE: {
    summary: 'Controls skill points per level and several knowledge skills.',
    formulas: [{ label: 'Base contribution', value: '2 skill points × INT' }],
    detail: [
      'The level-up formula counts base Intelligence plus trait modifiers only. A GAIN_INTELLIGENCE '
        + 'perk or a Mentats bonus raises the number on your character screen but does not add skill '
        + 'points at the next level.',
      'It also governs Science, First Aid, Doctor and Outdoorsman, and gates a lot of dialogue.',
    ],
    source: 'engine/src/character_editor.cc characterEditorUpdateLevel',
  },
  AGILITY: {
    summary: 'Action points and armour class, plus most weapon skills.',
    formulas: [
      { label: 'Action points', value: 'AGI ÷ 2 + 5' },
      { label: 'Armour class', value: 'AGI' },
    ],
    source: 'engine/src/stat.cc critterUpdateDerivedStats',
  },
  LUCK: {
    summary: 'Sets your critical hit chance, one for one.',
    formulas: [{ label: 'Critical chance', value: 'LUK' }],
    detail: ['Critical chance is exactly Luck, before Finesse and the More Criticals perk.'],
    source: 'engine/src/stat.cc critterUpdateDerivedStats',
  },
};

const DERIVED_EXPLANATIONS: Record<string, Explanation> = {
  MAXIMUM_HIT_POINTS: {
    summary: 'Base Strength + 2 × base Endurance + 15, plus banked hit-point gains.',
    detail: [
      'Each level banks Endurance ÷ 2 + 2 HP (2–7 at normal stat bounds), plus 4 per rank of Lifegiver, in the '
        + 'bonus stat rather than recomputed — which is why this number does not fall when Endurance does.',
    ],
    source: 'engine/src/stat.cc critterUpdateDerivedStats; pcAddExperienceWithOptions',
  },
  MAXIMUM_ACTION_POINTS: {
    summary: 'Base action points are Agility ÷ 2 + 5; bonuses and penalties are added afterward.',
    detail: ['Bruiser takes 2 off. If overweight, the live penalty is floor(overweight ÷ 40) + 1 AP; it is not stored in this number.'],
    source: 'engine/src/stat.cc critterUpdateDerivedStats, critterGetStat',
  },
  ARMOR_CLASS: {
    summary: 'Base armour class equals Agility; armour, perks, traits and combat state can change the total.',
    detail: ['Kamikaze removes the base value. During combat, unused action points can add temporary armour class; that live bonus is not stored here.'],
    source: 'engine/src/stat.cc critterUpdateDerivedStats, critterGetStat; trait.cc traitGetStatModifier',
  },
  MELEE_DAMAGE: {
    summary: 'Base melee damage is Strength − 5, never below 1; bonuses are added afterward.',
    detail: ['Heavy Handed adds 4. Bonus HtH Damage and other effects can be present in the stored bonus row.'],
    source: 'engine/src/stat.cc critterUpdateDerivedStats; trait.cc traitGetStatModifier; perk.cc perkAddEffect',
  },
  CARRY_WEIGHT: {
    summary: 'Base carry weight is 25 × Strength + 25 pounds; bonuses and traits alter the total.',
    detail: ['Small Frame removes 10 × base Strength. Strong Back and other effects are recorded in the stored bonus.'],
    source: 'engine/src/stat.cc critterUpdateDerivedStats; trait.cc traitGetStatModifier; perk.cc perkAddEffect',
  },
  SEQUENCE: {
    summary: 'Base sequence is 2 × Perception and determines who acts first in combat.',
    detail: ['Kamikaze adds 5. Earlier Sequence and other effects are recorded in the stored bonus.'],
    source: 'engine/src/stat.cc critterUpdateDerivedStats; perk.cc perkAddEffect',
  },
  HEALING_RATE: {
    summary: 'Base healing rate is Endurance ÷ 3, never below 1.',
    detail: ['Fast Metabolism adds 2. Faster Healing and similar effects are stored as bonuses. Resting by the hour heals a flat 14 per three hours through a separate path.'],
    source: 'engine/src/stat.cc critterUpdateDerivedStats; critter.cc critterHealByHours',
  },
  CRITICAL_CHANCE: {
    summary: 'Base critical chance equals Luck; bonuses and traits are added afterward.',
    detail: ['Finesse adds 10. More Criticals adds 5 per rank through the stored bonus.'],
    source: 'engine/src/stat.cc critterUpdateDerivedStats; trait.cc traitGetStatModifier; perk.cc perkAddEffect',
  },
  BETTER_CRITICALS: {
    summary: 'A modifier to the critical-effect roll; higher values produce harsher critical effects.',
    detail: ['Its recomputed base is 0. Heavy Handed subtracts 30; the Better Criticals perk adds 20 through the stored bonus.'],
    source: 'engine/src/stat.cc critterUpdateDerivedStats; combat.cc; trait.cc traitGetStatModifier; perk.cc perkAddEffect',
  },
  RADIATION_RESISTANCE: {
    summary: 'Base resistance is 2 × Endurance; the shown percentage also includes stored and read-time modifiers.',
    detail: ['Fast Metabolism removes the base. Rad Resistance writes +15 per rank into the stored bonus; Vault City Inoculations adds 10 when the stat is read.'],
    source: 'engine/src/stat.cc critterUpdateDerivedStats; critter.cc critterAdjustRadiation',
  },
  POISON_RESISTANCE: {
    summary: 'Base resistance is 5 × Endurance; the shown percentage also includes stored and read-time modifiers.',
    detail: ['Fast Metabolism removes the base. Snakeater writes +25 per rank into the stored bonus; Vault City Inoculations adds 10 when the stat is read.'],
    source: 'engine/src/stat.cc critterUpdateDerivedStats; critter.cc critterAdjustPoison',
  },
};

/**
 * Radiation, spelled out — this is one of the two the owner asked for by name.
 *
 * The band table is generated from `RADIATION_LEVELS` so the prose and the
 * numbers cannot drift.
 */
export function radiationExplanation(current: number, game: SaveGameType = 'fo2'): Explanation {
  const bands = RADIATION_LEVELS.map((level) => {
    const range = level.to === null ? `${level.from}+` : `${level.from}–${level.to}`;
    const effects = level.penalties.length
      ? level.penalties.map((p) => `${p.stat.replace(/_/g, ' ').toLowerCase()} ${p.amount}`).join(', ')
      : 'no penalty';
    return [range, level.name, effects];
  });
  return {
    summary: `Absorbed radiation, checked at midnight. Current count: ${current}.`,
    table: {
      columns: ['Rads', 'Level', 'SPECIAL penalty'],
      rows: bands,
    },
    detail: [
      'Crossing into a worse band schedules its penalties a few hours after the midnight check.',
      'Penalties change bonus stats, not base stats, and are removed '
        + 'seven in-game days later. So a save with rads in it may already be carrying negative bonus '
        + 'stats — check the bonus column before concluding a stat is wrong.',
      'A failed Endurance roll at the midnight check bumps you one band worse than your count alone '
        + 'would give. If any of the six affected SPECIAL stats is driven below 1 this way, the character dies '
        + '(Luck is not affected by radiation).',
      // fallout1-ce's `rad_bonus` and `critter_check_rads` thresholds are the
      // same numbers as Fallout 2's `gRadiationEffectPenalties` and
      // `critterCheckRadiationEvent`, checked table row by table row — so the
      // bands and penalties above serve both games. The rad *indicator* does
      // not: `RADATION_INDICATOR_THRESHOLD` is in engine/src/interface.h and
      // has no counterpart anywhere in fallout1-ce, whose interface bar has no
      // such icon. So that sentence is Fallout 2's alone.
      game === 'fo1'
        ? `Incoming rads are reduced by your radiation resistance first. Current count: ${current}.`
        : `Incoming rads are reduced by your radiation resistance first. The interface shows the rad icon `
          + `above ${RADIATION_INDICATOR_THRESHOLD}. Current count: ${current}.`,
    ],
    source: game === 'fo1'
      ? 'fallout1-ce src/game/critter.cc critter_check_rads, process_rads, critter_adjust_rads'
      : 'engine/src/critter.cc critterCheckRadiationEvent, radiationProcess, critterAdjustRadiation',
  };
}

/** Poison, spelled out — the other one asked for by name. */
export function poisonExplanation(current: number, game: SaveGameType = 'fo2'): Explanation {
  return {
    summary: `Damage that burns itself out over time. Current count: ${current}.`,
    formulas: [
      { label: 'Damage per tick', value: `${POISON_DAMAGE_PER_TICK} HP` },
      { label: 'Count decay', value: `−${POISON_DECAY_PER_TICK} per tick` },
      { label: 'Tick interval', value: '10 × (505 − 5 × poison) game ticks' },
    ],
    detail: [
      'The higher the count, the faster the ticks come — the interval is 10 × (505 − 5 × poison) game '
        + 'ticks, and there is only ever one tick pending at a time.',
      'At 5 hit points or less a poison tick stops the current queue-processing pass, but the next poison '
        + 'event has already been scheduled. The chain continues and can kill the character.',
      'Incoming poison is reduced by your poison resistance; the per-tick decay is not.',
      'Unlike radiation, poison has no levels or stat penalties in this engine.',
    ],
    // Every number above is identical in both engines — fallout1-ce's
    // `critter_adjust_poison` schedules `10 * (505 - 5 * poison)` and
    // `critter_check_poison` does `-2` poison and `-1` hit point, the same as
    // Fallout 2's. Only the symbol names differ, so only the citation forks.
    source: game === 'fo1'
      ? 'fallout1-ce src/game/critter.cc critter_check_poison, critter_adjust_poison'
      : 'engine/src/critter.cc poisonEventProcess, critterAdjustPoison',
  };
}

/**
 * Traits, rewritten. The owner found the old wording opaque, and it was: it
 * described the mechanism and not the consequence.
 */
export const TRAIT_EXPLANATION: Explanation = {
  summary: 'Traits modify what the game displays; their bonuses are not baked into stored stats.',
  detail: [
    'So a Gifted character whose file says Strength 6 has Strength 7 on the character screen. Take '
      + 'Gifted away and the file still says 6 — but the screen now says 6 too, and everything built '
      + 'on Strength moves with it.',
    'Traits also reach the derived stats the same way: Small Frame takes 10 × Strength off carry '
      + 'weight, Kamikaze zeroes armour class, Fast Metabolism zeroes both resistances. None of that '
      + 'is stored either.',
    'Two traits maximum, the same as character creation. Skilled is the odd one: it does not touch a '
      + 'stat at all, it gives 5 more skill points a level and pushes perks from every 3 levels to '
      + 'every 4.',
  ],
  source: 'engine/src/trait.cc traitGetStatModifier, traitGetSkillModifier; character_editor.cc characterEditorUpdateLevel',
};

/** Skills: the points-versus-percent question, and the cap. */
export const SKILL_EXPLANATION: Explanation = {
  summary: 'The file stores points; the game calculates the displayed percentage.',
  table: {
    columns: ['Displayed skill', 'Points per +1%'],
    rows: SKILL_COST_BANDS.map(([from, to, cost]) => [from + (to === null ? '+' : `–${to}`), String(cost)]),
  },
    detail: [
    'Displayed skill combines its starting value, governing stats, spent points, tag bonus, traits and perks.',
    `The engine clamps the result at ${SKILL_DISPLAY_CAP}%, at the very end, after everything else. `
      + 'Points past the clamp do nothing at all — the engine will not even let you spend them. That is '
      + 'why two different point totals can both read 300%, and why the uncapped figure is shown next '
      + 'to it here.',
    'A tagged skill counts its points twice and gets 20 percentage points on top. The fourth tag, the '
      + 'one the Tag! perk grants, gets the doubling but not the 20.',
  ],
  source: 'engine/src/skill.cc skillGetValue, skillsGetCost, skillAdd',
};

/**
 * The Fallout 1 versions of the three panels whose *numbers* change with the
 * game.
 *
 * Kept as whole alternative texts rather than as templated sentences, because
 * the differences are not all numeric: Fallout 1 has no cost curve to tabulate
 * at all, its Skilled trait pays in skill percentages rather than skill points,
 * and its tag bonus has no Tag!-perk exception. A panel that swapped "300" for
 * "200" and stopped there would be confidently wrong in three places.
 */
export const SKILL_EXPLANATION_FO1: Explanation = {
  summary: 'The file stores points; the game calculates the displayed percentage.',
  formulas: [
    { label: 'Cost', value: '1 skill point per +1%, at every level' },
    { label: 'Two-stat skills', value: '(stat1 + stat2) × modifier ÷ 2' },
  ],
  detail: [
    'Displayed skill combines its starting value, governing stats, spent points, tag bonus, traits and perks.',
    `The engine clamps the result at ${forGame('fo1').skillMaximum}%, at the very end. Fallout 1 has no `
      + 'rising cost curve — one skill point is one percent all the way up, and the engine simply '
      + 'refuses to spend any once the cap is reached.',
    'A skill governed by two stats averages them: the engine adds the two, multiplies by the skill\'s '
      + 'modifier and halves the result — it does not add each stat\'s contribution separately the way '
      + 'Fallout 2 does.',
    'A tagged skill counts its points twice and gets 20 percentage points on top, with no exception for '
      + 'the fourth slot.',
  ],
  source: 'fallout1-ce src/game/skill.cc skill_level, skill_inc_point',
};

export const LEVEL_EXPLANATION_FO1: Explanation = {
  summary: 'Experience sets your level; each new level grants skill points and sometimes a perk.',
  formulas: [
    { label: 'XP minimum', value: '500 × level × (level − 1)' },
    { label: 'Skill points', value: '5 + 2 × INT + 2 × Educated (− 5 if Gifted)' },
    { label: 'Perk cadence', value: 'every 3 levels; every 4 with Skilled' },
    { label: 'Level cap', value: String(forGame('fo1').pcLevelMax) },
  ],
  detail: [
    'Skill points per level are 5, plus 2 per point of Intelligence, plus 2 per rank of Educated, minus '
      + '5 if you are Gifted — floored at zero. Skilled grants no skill points in Fallout 1; it gives '
      + '+10% to every skill instead, and pushes perks to every 4 levels.',
    'The Intelligence in that formula is your stored base, with no trait modifier at all — so Gifted\'s '
      + '+1 Intelligence does not offset the 5 points it costs you.',
    `The level cap is ${forGame('fo1').pcLevelMax}. The experience thresholds below it are the same `
      + 'triangular curve Fallout 2 uses, written out as a table in the engine rather than computed.',
    'A perk slot lands on every level whose number divides by 3 — by 4 if you are Skilled. Fallout 1 '
      + 'sets the owed count to one rather than adding to it, so crossing several perk levels with the '
      + 'character screen closed still owes exactly one pick.',
  ],
  source: 'fallout1-ce src/game/stat.cc stat_pc_min_exp; editor.cc UpdateLevel',
};

export const PERK_EXPLANATION_FO1: Explanation = {
  summary: 'Perks cost a slot and must pass their level, stat, and skill requirements.',
  detail: [
    'The picker lists what your character actually qualifies for, with the requirements written out. '
      + 'The comparison uses your character-screen SPECIAL and your displayed skill percentages, which '
      + 'is what the engine compares.',
    'Fallout 1 has 64 perks against Fallout 2\'s 119, and each carries a single skill requirement '
      + 'rather than a pair with an AND/OR mode. A negative requirement in the table is not a "must be '
      + 'below" test the way it is in Fallout 2 — perk_can_add compares with a plain "less than", so a '
      + 'negative threshold is simply always satisfied.',
    `Ranks stack up to each perk's own maximum. The ${maxSelectablePerks('fo1')}-perk limit is not a `
      + 'limit on the picker: perk_can_add never counts your perks. It is UpdateLevel that stops '
      + `*awarding* a new pick once ${maxSelectablePerks('fo1')} distinct perks are held, so levelling `
      + 'past that point earns no further slots even though the list would still show qualifying perks.',
  ],
  source: 'fallout1-ce src/game/perk.cc perk_can_add, perk_data; editor.cc UpdateLevel',
};

/** The right variant of a panel for the game that wrote the open save. */
export function skillExplanationFor(game: SaveGameType): Explanation {
  return game === 'fo1' ? SKILL_EXPLANATION_FO1 : SKILL_EXPLANATION;
}
export function levelExplanationFor(game: SaveGameType): Explanation {
  return game === 'fo1' ? LEVEL_EXPLANATION_FO1 : LEVEL_EXPLANATION;
}
export function perkExplanationFor(game: SaveGameType): Explanation {
  return game === 'fo1' ? PERK_EXPLANATION_FO1 : PERK_EXPLANATION;
}
export function traitExplanationFor(game: SaveGameType): Explanation {
  if (game !== 'fo1') return TRAIT_EXPLANATION;
  return {
    ...TRAIT_EXPLANATION,
    detail: [
      TRAIT_EXPLANATION.detail![0]!,
      TRAIT_EXPLANATION.detail![1]!,
      'Two traits maximum, the same as character creation. Fallout 1 has Night Person where Fallout 2 '
        + 'has Sex Appeal — the same slot in the file, a different trait — and its Skilled gives +10% to '
        + 'every skill rather than 5 skill points a level.',
      'Night Person shifts Perception and Intelligence by the time of day, which the character screen '
        + 'resolves when you open it; this panel shows the stored values.',
    ],
    source: 'fallout1-ce src/game/trait.cc trait_adjust_stat, trait_adjust_skill; editor.cc UpdateLevel',
  };
}

/** What each final skill percentage is actually consumed by. */
const SKILL_USE_EXPLANATIONS: Record<string, Explanation> = {
  SMALL_GUNS: {
    summary: 'Sets the starting hit chance for conventional pistols, SMGs, rifles and shotguns.',
    detail: ['Combat then applies range, lighting, aimed-shot, target armour class, line-of-fire, weapon accuracy and minimum-Strength modifiers.'],
    source: 'engine/src/item.cc weaponGetSkillForHitMode; combat.cc attackDetermineToHit',
  },
  BIG_GUNS: {
    summary: 'Sets the starting hit chance for weapons carrying the engine’s Big Gun flag.',
    detail: ['Combat applies the same range, lighting, aimed-shot, armour-class, line-of-fire and Strength modifiers after this percentage.'],
    source: 'engine/src/item.cc weaponGetSkillForHitMode; combat.cc attackDetermineToHit',
  },
  ENERGY_WEAPONS: {
    summary: 'Sets the starting hit chance for laser, plasma and electrical guns.',
    detail: ['The engine selects this skill from the weapon’s damage type, then applies combat’s range, lighting, aimed-shot, armour-class and Strength modifiers.'],
    source: 'engine/src/item.cc weaponGetSkillForHitMode; combat.cc attackDetermineToHit',
  },
  UNARMED: {
    summary: 'Sets unarmed hit chance and helps unlock stronger punch and kick attacks.',
    detail: ['Combat uses it as the starting hit chance when no weapon supplies another skill. With HtH Evade, one twelfth of Unarmed can also contribute to armour class.'],
    source: 'engine/src/combat.cc attackDetermineToHit, unarmedGetPunchHitMode, unarmedGetKickHitMode; stat.cc critterGetStat',
  },
  MELEE_WEAPONS: {
    summary: 'Sets the starting hit chance for hand-held melee weapons.',
    detail: ['Combat then applies target armour class, aimed-location penalties, lighting, weapon accuracy and any minimum-Strength shortfall.'],
    source: 'engine/src/item.cc weaponGetSkillForHitMode; combat.cc attackDetermineToHit',
  },
  THROWING: {
    summary: 'Sets the starting hit chance for thrown weapons and thrown attacks.',
    detail: ['Combat then applies distance, lighting, aimed-location, line-of-fire, armour-class and Strength modifiers.'],
    source: 'engine/src/item.cc weaponGetSkillForHitMode; combat.cc attackDetermineToHit',
  },
  FIRST_AID: {
    summary: 'Rolls to restore a small amount of HP to a living, non-robotic target.',
    detail: ['A use takes 30 minutes. Only three successful uses fit in the rolling 24-hour usage ledger. Healer increases the HP range; Medic and Vault City Training raise the skill.'],
    source: 'engine/src/skill.cc skillUse, skillRoll, skillGetFreeUsageSlot; perk.cc perkGetSkillModifier',
  },
  DOCTOR: {
    summary: 'Rolls to restore more HP and separately repair blindness or crippled limbs.',
    detail: ['Each injury gets its own roll and adds an hour to the treatment. Doctor shares the three-use/24-hour limit pattern; robotic targets fail this medical path.'],
    source: 'engine/src/skill.cc skillUse, skillRoll, skillGetFreeUsageSlot',
  },
  SNEAK: {
    summary: 'Periodic rolls decide whether active Sneak is currently working.',
    detail: ['A successful state can add 30 to Steal rolls. Higher Sneak shortens the retry interval after a failed check. Ghost can add 20 in sufficiently low light; because light is live map state, that conditional bonus is not included in this save-side total.'],
    source: 'engine/src/critter.cc sneakEventProcess; skill.cc skillRoll; perk.cc perkGetSkillModifier',
  },
  LOCKPICK: {
    summary: 'Supplies the percentage used by lock scripts when you use Lockpick on an item or scenery.',
    detail: ['The core validates the target and dispatches the action; each lock’s script chooses its modifier and success consequence. A suitable best-skilled companion may perform the attempt.'],
    source: 'engine/src/actions.cc actionUseSkill; interpreter_extra.cc opRollVsSkill; skill.cc skillRoll',
  },
  STEAL: {
    summary: 'Controls both taking an item successfully and whether the target catches you.',
    detail: ['Item size, repeated attempts and facing penalize the attempt; a knocked-down target helps. The initial steal chance is capped at 95. Working Sneak adds 30, and the target then makes a separate catch roll.'],
    source: 'engine/src/skill.cc skillsPerformStealing, skillRoll; inventory.cc',
  },
  TRAPS: {
    summary: 'Supplies the roll used by trap scripts to detect, disarm or trigger a trap.',
    detail: ['The core dispatches the selected target; object and map scripts decide the modifier and result. Party substitution can use a qualifying companion’s better skill.'],
    source: 'engine/src/actions.cc actionUseSkill; proto_instance.cc; interpreter_extra.cc opRollVsSkill',
  },
  SCIENCE: {
    summary: 'Supplies rolls and thresholds for computers, terminals, technical analysis and scripted checks.',
    detail: ['Most outcomes live in the target’s script, which chooses the modifier. The core permits Science on scenery, robots and certain configured critter targets.'],
    source: 'engine/src/actions.cc actionUseSkill; interpreter_extra.cc opRollVsSkill; skill.cc skillRoll',
  },
  REPAIR: {
    summary: 'Supplies scripted repair checks and can directly heal damaged robotic critters.',
    detail: ['Robot repair can restore HP and damaged parts, takes 30 minutes per attempt, and uses the three-use/24-hour ledger. Mr. Fixit raises the skill.'],
    source: 'engine/src/actions.cc actionUseSkill; skill.cc skillUse, skillGetFreeUsageSlot',
  },
  SPEECH: {
    summary: 'Dialogue scripts compare or roll this percentage to unlock and resolve conversation choices.',
    detail: ['The script supplies any difficulty modifier, so there is no single universal Speech threshold. Non-Steal rolls may use a qualifying companion whose best skill is Speech.'],
    source: 'engine/src/interpreter_extra.cc opRollVsSkill, opHasSkill; skill.cc skillRoll',
  },
  BARTER: {
    summary: 'Changes shop prices by comparing the party’s best Barter with the merchant’s Barter.',
    detail: ['The engine scales the merchant table by (160 + merchant Barter) ÷ (160 + party Barter), then applies the global barter modifier and Master Trader.'],
    source: 'engine/src/inventory.cc barterComputeTablesValue; party.cc partyGetBestSkillValue',
  },
  GAMBLING: {
    summary: 'Gambling scripts use this percentage for game-specific win, loss and payout checks.',
    detail: ['The core provides the value and generic skill roll; each gambling script supplies its own modifier and stakes.'],
    source: 'engine/src/interpreter_extra.cc opRollVsSkill, opHasSkill; skill.cc skillRoll',
  },
  OUTDOORSMAN: {
    summary: 'Helps the party detect random encounters before being forced into them.',
    detail: ['The world map uses the party’s best Outdoorsman, adds 20 for a carried motion sensor, caps that subtotal at 95, then applies the terrain encounter modifier and rolls 1–100.'],
    source: 'engine/src/worldmap.cc wmRndEncounterOccurred; party.cc partyGetBestSkillValue',
  },
};

export function skillUseExplanation(name: string): Explanation | null {
  return SKILL_USE_EXPLANATIONS[name] ?? null;
}

/** Levelling: XP, skill points, perk slots. */
export const LEVEL_EXPLANATION: Explanation = {
  summary: 'Experience sets your level; each new level grants skill points and sometimes a perk.',
  formulas: [
    { label: 'XP minimum', value: '500 × level × (level − 1)' },
    { label: 'Skill points', value: '5 + 2 × INT + Educated + traits' },
    { label: 'Perk cadence', value: 'every 3 levels; every 4 with Skilled' },
  ],
  detail: [
    'Skill points per level are 5, plus 2 per point of Intelligence, plus 2 per rank of Educated, plus '
      + '5 if you are Skilled, minus 5 if you are Gifted. The unspent pool is capped at 99, level by '
      + 'level — you cannot bank more than that.',
    'The Intelligence in that formula is your base plus traits. Perks and drugs that raise the number '
      + 'on your character screen do not count.',
    'A perk slot lands on every level whose number divides by 3 — by 4 if you are Skilled. Adding '
      + 'Skilled does not take a perk back, but it re-phases every slot after it.',
    'Setting the level here moves experience to that level\'s minimum. You can then raise experience '
      + 'anywhere inside the level\'s band.',
  ],
  source: 'engine/src/stat.cc pcGetExperienceForLevel; character_editor.cc characterEditorUpdateLevel, characterEditorGetLevelsPerPerk',
};

/** Perks: what the picker is enforcing. */
export const PERK_EXPLANATION: Explanation = {
  summary: 'Perks cost a slot and must pass their level, stat, and skill requirements.',
  detail: [
    'The picker lists what your character actually qualifies for, with the requirements written out. '
      + 'The comparison uses your character-screen SPECIAL and your displayed skill percentages, which '
      + 'is what the engine compares.',
    'A handful of requirements are the other way round — the stat has to be *below* a number. Those are '
      + 'shown as "below N" rather than silently inverted.',
    'Perks the game never offers in the level-up dialog — addictions, armour bonuses, the GAIN_ perks '
      + 'that scripts and items hand out — are not in the engine-mode list at all. Expert mode has them.',
    'Ranks stack up to each perk\'s own maximum, and the character screen stops offering anything once '
      + '37 distinct perks are held.',
  ],
  source: 'engine/src/perk.cc perkCanAddAtLevel, gPerkDescriptions',
};

/** Globals: why most of them have no description here. */
export const GVAR_EXPLANATION: Explanation = {
  summary: 'Quest scripts read and write these numbered states; most are not simple on/off switches.',
  detail: [
    'Quest flags are state machines, not switches: a flag runs 0 → 1 → 2 → 3 and different scripts '
      + 'branch on each value. Writing a number no script ever writes can leave a quest unfinishable.',
    'Where the meaning has actually been established, it is shown. Everywhere else you get the name and '
      + 'the value and nothing more — a confidently wrong description of a quest flag is worse than none.',
  ],
  source: 'engine/src/scripts.cc scriptsSaveGameGlobalVars; the install\'s own data/vault13.gam',
};

/** Hit points. */
export const HIT_POINTS_EXPLANATION: Explanation = {
  summary: 'Your current hit points, stored on the character object rather than in the stat block.',
  detail: [
    'Nothing recomputes it on load, so a value set here sticks — but the next heal or damage clamps it '
      + 'back to your maximum, so setting it above that buys one screenful of a big number.',
  ],
  source: 'engine/src/critter.cc critterAdjustHitPoints',
};

/** The one-line note for a SPECIAL stat, or null when there is nothing to add. */
export function statExplanation(name: string): Explanation | null {
  return STAT_EXPLANATIONS[name] ?? null;
}

/** The note for a derived stat. */
export function derivedExplanation(name: string): Explanation | null {
  return DERIVED_EXPLANATIONS[name] ?? null;
}

/** The note for one of the PC-stat progress fields. */
export function pcStatExplanation(name: string): Explanation | null {
  switch (name) {
    case 'LEVEL':
    case 'EXPERIENCE':
      return LEVEL_EXPLANATION;
    case 'UNSPENT_SKILL_POINTS':
      return {
        summary: 'Skill points you have earned and not yet spent. Capped at 99 by the level-up code.',
        detail: ['Spending them costs more per percent the higher the skill already is — see any skill row.'],
        source: 'engine/src/character_editor.cc characterEditorUpdateLevel; skill.cc skillAdd',
      };
    case 'KARMA':
      return {
        summary: 'A single running number the scripts read to decide how the world treats you.',
        detail: ['Named reputations — Childkiller, Berserker, town reputations — are separate globals, on the Globals tab.'],
        source: 'engine/src/stat.cc gPcStatDescriptions',
      };
    case 'REPUTATION':
      return {
        summary: 'Ranges −20 to 20. Most of what players call reputation is actually a game global, not this.',
        source: 'engine/src/stat.cc gPcStatDescriptions',
      };
    default:
      return null;
  }
}
